/**
 * Pool Configuration Manager
 * ─────────────────────────────────────────────────────────────────────────────
 * Administrator-only page that lets a super-admin edit the signup-time
 * overflow thresholds (`max_clients_per_pool`, `max_pool_size_mb`) and any
 * other tunables stored in `WebApp.tbl_pool_config` (a key-value table).
 *
 * NO cascade — saving a row only touches that single tbl_pool_config row.
 * The matching ClientMaster / Industry Master / UAMS data is NOT modified.
 *
 * Per-industry TemplateDB editing lives on the **Industry Master** page
 * (each industry row has its own `TemplateDB` field).
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Database, RefreshCw, Save, X, AlertCircle, Edit3, Plus } from 'lucide-react';
import { erpToast } from './erp/ERPToast';
import { API, apiFetch } from '../lib/api';

// Friendly labels for known config keys (everything else is shown verbatim).
const KEY_META = {
  max_clients_per_pool: { label: 'Max Clients per Pool',  hint: 'Signup creates a new pool DB after this many clients.', defaultVal: '100' },
  max_pool_size_mb:     { label: 'Max Pool Size (MB)',    hint: 'Signup creates a new pool DB after the current pool exceeds this size.', defaultVal: '1024' },
  default_template_db:  { label: 'Default Template DB',   hint: 'Fallback TemplateDB when an industry doesn’t declare its own.',         defaultVal: 'RC26ERP_DEV' },
  pool_id_prefix:       { label: 'Pool ID Prefix',        hint: 'String prepended when minting a new pool ID.',                          defaultVal: 'POOL_' }
};

export default function PoolConfigManager() {
  const [rows, setRows]         = useState([]);
  const [pkColumn, setPkColumn] = useState('config_key');
  const [loading, setLoading]   = useState(false);
  const [editing, setEditing]   = useState(null);   // key currently being edited
  const [draft, setDraft]       = useState('');
  const [saving, setSaving]     = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiFetch(`${API}/licensor/pool-config`);
      const d = await r.json();
      if (!r.ok || d.error) erpToast.error(`Load failed: ${d.error || r.status}`);
      else {
        setRows(Array.isArray(d) ? d : (Array.isArray(d.rows) ? d.rows : []));
        if (d.pkColumn) setPkColumn(d.pkColumn);
      }
    } catch (e) { erpToast.error(`Load failed: ${e.message}`); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const startEdit = (row) => { setEditing(row[pkColumn]); setDraft(String(row.config_value ?? '')); };
  const cancelEdit = () => { setEditing(null); setDraft(''); };

  const save = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      const r = await apiFetch(`${API}/licensor/pool-config/${encodeURIComponent(editing)}`, {
        method:  'PUT',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ config_value: draft })
      });
      const d = await r.json();
      if (!r.ok || d.error) { erpToast.error(`Save failed: ${d.error || r.status}`); }
      else {
        erpToast.success(`Pool config "${editing}" updated`);
        cancelEdit();
        await load();
      }
    } catch (e) { erpToast.error(`Save failed: ${e.message}`); }
    setSaving(false);
  };

  // Find missing known keys so the operator can seed defaults with one click.
  const existingKeys = new Set(rows.map(r => String(r[pkColumn]).toLowerCase()));
  const missingKnown = Object.keys(KEY_META).filter(k => !existingKeys.has(k.toLowerCase()));

  const seedKey = async (key) => {
    setSaving(true);
    try {
      const r = await apiFetch(`${API}/licensor/pool-config/${encodeURIComponent(key)}`, {
        method:  'PUT',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ config_value: KEY_META[key].defaultVal, create_if_missing: true })
      });
      const d = await r.json();
      if (!r.ok || d.error) erpToast.error(`Seed failed: ${d.error || r.status}`);
      else { erpToast.success(`Seeded ${key} = ${KEY_META[key].defaultVal}`); await load(); }
    } catch (e) { erpToast.error(`Seed failed: ${e.message}`); }
    setSaving(false);
  };

  return (
    <div data-testid="pool-config-manager" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {/* Help banner */}
      <div style={{
        display: 'flex', alignItems: 'flex-start', gap: 10,
        padding: '10px 14px', borderRadius: 6,
        background: 'var(--erp-grid-row-hover, #eff6ff)',
        border: '1px solid var(--erp-border, #bfdbfe)',
        fontSize: 11, color: '#1e40af', lineHeight: 1.55
      }}>
        <AlertCircle size={14} style={{ flexShrink: 0, marginTop: 2 }} />
        <div>
          <strong>Pool Configuration</strong> — signup-time tunables stored in <code>WebApp.tbl_pool_config</code>.
          The defaults are <code>100 clients / 1024 MB</code> per pool. Saving a row touches{' '}
          <strong>only this table</strong> — no cascade to ClientMaster, UAMS, or Industry Master.
          Per-industry <code>TemplateDB</code> overrides live on the <strong>Industry Master</strong> page.
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Database size={16} />
        <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700 }}>Pool Configuration ({rows.length})</h3>
        <button
          type="button" className="erp-btn"
          onClick={load}
          data-testid="pool-config-refresh-btn"
          style={{ marginLeft: 'auto', fontSize: 11 }}
          title="Refresh"
        >
          <RefreshCw size={11} style={{ marginRight: 4 }} />
          Refresh
        </button>
      </div>

      {/* Missing known keys → 1-click seed */}
      {missingKnown.length > 0 && (
        <div style={{
          padding: '8px 12px', borderRadius: 6,
          background: 'var(--surface-2, #fefce8)', border: '1px solid #facc15',
          fontSize: 11, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap'
        }}>
          <strong>Seed defaults:</strong>
          {missingKnown.map(k => (
            <button
              key={k}
              type="button"
              className="erp-btn"
              onClick={() => seedKey(k)}
              disabled={saving}
              data-testid={`pool-seed-${k}`}
              style={{ fontSize: 10, padding: '3px 8px' }}
              title={KEY_META[k].hint}
            >
              <Plus size={10} style={{ marginRight: 3 }} />
              {KEY_META[k].label} ({KEY_META[k].defaultVal})
            </button>
          ))}
        </div>
      )}

      <div style={{ border: '1px solid var(--erp-border, #e2e8f0)', borderRadius: 6, overflow: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, minWidth: 600 }}>
          <thead>
            <tr style={{ background: 'var(--erp-grid-header, #f1f5f9)', textAlign: 'left' }}>
              <Th>Key</Th>
              <Th>Value</Th>
              <Th>Description</Th>
              <Th>Updated</Th>
              <Th>Actions</Th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={5} style={{ padding: 24, textAlign: 'center', color: '#94a3b8' }}>Loading…</td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={5} style={{ padding: 24, textAlign: 'center', color: '#94a3b8' }}>
                No config rows yet. Use the seed buttons above to add defaults.
              </td></tr>
            )}
            {!loading && rows.map(row => {
              const rowKey = String(row[pkColumn]);
              const isEditing = editing === row[pkColumn];
              const meta = KEY_META[rowKey] || {};
              return (
                <tr key={rowKey} style={{ borderTop: '1px solid var(--erp-border)' }} data-testid={`pool-row-${rowKey}`}>
                  <Td>
                    <code style={{ fontSize: 11, fontWeight: 600 }}>{rowKey}</code>
                    {meta.label && <div style={{ fontSize: 10, color: '#64748b', marginTop: 1 }}>{meta.label}</div>}
                  </Td>
                  <Td>
                    {isEditing
                      ? <input
                          value={draft}
                          onChange={e => setDraft(e.target.value)}
                          autoFocus
                          data-testid={`pool-input-${rowKey}`}
                          style={inpStyle}
                          onKeyDown={e => { if (e.key === 'Enter') save(); if (e.key === 'Escape') cancelEdit(); }}
                        />
                      : <strong style={{ fontFamily: 'monospace' }}>{row.config_value ?? '(empty)'}</strong>}
                  </Td>
                  <Td><span style={{ fontSize: 11, color: '#64748b' }}>{meta.hint || '—'}</span></Td>
                  <Td>
                    <span style={{ fontSize: 10, color: '#94a3b8' }}>
                      {row.updated_at ? new Date(row.updated_at).toLocaleString() : '—'}
                    </span>
                    {row.updated_by && <div style={{ fontSize: 10, color: '#94a3b8' }}>{row.updated_by}</div>}
                  </Td>
                  <Td>
                    {isEditing ? (
                      <div style={{ display: 'flex', gap: 4 }}>
                        <button type="button" className="erp-btn erp-btn-primary" onClick={save} disabled={saving}
                                data-testid={`pool-save-${rowKey}`}
                                style={{ fontSize: 10, padding: '3px 8px' }}>
                          <Save size={10} style={{ marginRight: 3 }} />
                          {saving ? 'Saving…' : 'Save'}
                        </button>
                        <button type="button" className="erp-btn" onClick={cancelEdit} disabled={saving}
                                data-testid={`pool-cancel-${rowKey}`}
                                style={{ fontSize: 10, padding: '3px 8px' }}>
                          <X size={10} style={{ marginRight: 3 }} />
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <button type="button" className="erp-btn" onClick={() => startEdit(row)}
                              data-testid={`pool-edit-${rowKey}`}
                              style={{ fontSize: 10, padding: '3px 8px' }}>
                        <Edit3 size={10} style={{ marginRight: 3 }} />
                        Edit
                      </button>
                    )}
                  </Td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const inpStyle = {
  width: '100%', fontSize: 12, padding: '4px 8px',
  border: '1px solid var(--erp-border, #cbd5e1)', borderRadius: 4,
  background: 'var(--surface, #fff)', color: 'var(--text-primary, #1e293b)',
  fontFamily: 'monospace'
};
function Th({ children, align = 'left' }) {
  return <th style={{ padding: '8px 10px', fontWeight: 700, fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted, #64748b)', textAlign: align }}>{children}</th>;
}
function Td({ children, align = 'left' }) {
  return <td style={{ padding: '6px 10px', textAlign: align, color: 'var(--text-primary, #1e293b)', fontSize: 12 }}>{children}</td>;
}
